import React from "react";
import {
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TableContainer,
    Typography,
} from "@mui/material";

export const sortAdmissionCategoriesByPriority = (list, categoryKey) => {
    const categoryPriorityMap = new Map();
    list.forEach((item) => {
        const category = item[categoryKey];
        if (!category) return;
        const priority = item.priority ?? Number.MAX_SAFE_INTEGER;
        if (
            !categoryPriorityMap.has(category) ||
            priority < categoryPriorityMap.get(category)
        ) {
            categoryPriorityMap.set(category, priority);
        }
    });

    return (categories) =>
        [...categories].sort((a, b) => {
            const priorityDiff =
                (categoryPriorityMap.get(a) ?? Number.MAX_SAFE_INTEGER) -
                (categoryPriorityMap.get(b) ?? Number.MAX_SAFE_INTEGER);
            if (priorityDiff !== 0) return priorityDiff;
            return a.localeCompare(b, undefined, { sensitivity: "base" });
        });
};

export const GroupedColumnTable = ({ data }) => {
    // Group data by schoolName, then by program_short_name, then by program_specialization_short_name
    const schoolGroups = data.reduce((acc, item) => {
        if (!acc[item.schoolName]) {
            acc[item.schoolName] = {};
        }
        if (!acc[item.schoolName][item.program_short_name]) {
            acc[item.schoolName][item.program_short_name] = {};
        }
        if (!acc[item.schoolName][item.program_short_name][item.program_specialization_short_name]) {
            acc[item.schoolName][item.program_short_name][item.program_specialization_short_name] = [];
        }
        acc[item.schoolName][item.program_short_name][item.program_specialization_short_name].push(item);
        return acc;
    }, {});

    const feeCategories = sortAdmissionCategoriesByPriority(
        data,
        "fee_admission_category_short_name"
    )([
        ...new Set(data.map((item) => item.fee_admission_category_short_name)),
    ]);

    return (
        <TableContainer sx={{ paddingBottom: "30px" }}>
            <Table sx={{ border: "1px solid #ddd" }}>
                {/* Table Head */}
                <TableHead>
                    <TableRow sx={{ backgroundColor: "#376a7d" }}>
                        <TableCell
                            rowSpan={2}
                            sx={{
                                color: "#fff",
                                fontWeight: "bold",
                                border: "1px solid #fff",
                                fontSize: "14px",
                            }}
                        >
                            School Name
                        </TableCell>
                        <TableCell
                            rowSpan={2}
                            sx={{
                                color: "#fff",
                                fontWeight: "bold",
                                border: "1px solid #fff",
                                fontSize: "14px",
                            }}
                        >
                            Program
                        </TableCell>
                        <TableCell
                            rowSpan={2}
                            sx={{
                                color: "#fff",
                                fontWeight: "bold",
                                border: "1px solid #fff",
                                fontSize: "14px",
                            }}
                        >
                            Specialization
                        </TableCell>
                        {feeCategories.map((category) => (
                            <TableCell
                                key={category}
                                colSpan={3}
                                align="center"
                                sx={{
                                    color: "#fff",
                                    fontWeight: "bold",
                                    border: "1px solid #fff",
                                    fontSize: "15px",
                                    padding: "10px"
                                }}
                            >
                                {category === "MGT" ? "Management" : category}
                            </TableCell>
                        ))}
                    </TableRow>
                    <TableRow sx={{ backgroundColor: "#376a7d", fontSize: "14px" }}>
                        {feeCategories.map((category) => (
                            <React.Fragment key={category}>
                                <TableCell
                                    sx={{
                                        color: "#fff",
                                        fontWeight: "bold",
                                        border: "1px solid #fff",
                                        fontSize: "14px",
                                        padding: "10px",
                                        textAlign: "center"
                                    }}
                                >
                                    Intake
                                </TableCell>
                                <TableCell
                                    sx={{
                                        color: "#fff",
                                        fontWeight: "bold",
                                        border: "1px solid #fff",
                                        fontSize: "14px",
                                        padding: "10px",
                                        textAlign: "center"
                                    }}
                                >
                                    Admitted
                                </TableCell>
                                <TableCell
                                    sx={{
                                        color: "#fff",
                                        fontWeight: "bold",
                                        border: "1px solid #fff",
                                        fontSize: "14px",
                                        padding: "10px",
                                        textAlign: "center"
                                    }}
                                >
                                    Vacant
                                </TableCell>
                            </React.Fragment>
                        ))}
                    </TableRow>
                </TableHead>

                {/* Table Body */}
                <TableBody>
                    {Object.entries(schoolGroups).map(([schoolName, programs]) => (
                        <React.Fragment key={schoolName}>
                            {Object.entries(programs).map(([programName, specializations]) => (
                                <React.Fragment key={programName}>
                                    {Object.entries(specializations).map(([specializationName, rows]) => {
                                        // Combine data for different fee categories into a single row
                                        const combinedData = feeCategories.reduce((acc, category) => {
                                            const row = rows.find(
                                                (r) => r.fee_admission_category_short_name === category
                                            );
                                            acc[category] = row
                                                ? {
                                                    intake: row.intake,
                                                    vacant: row.vacant,
                                                    admitted: row.admitted,
                                                }
                                                : { intake: "-", vacant: "-", admitted: "-" };
                                            return acc;
                                        }, {});
                                        
                                        const hasNegativeAdmitted = Object.values(combinedData).some(
                                            (data) => data.vacant !== "-" && data.vacant < 0
                                        );

                                        return (
                                            <TableRow
                                                key={`${schoolName}-${programName}-${specializationName}`}
                                                sx={{ borderBottom: "1px solid #ddd", backgroundColor: hasNegativeAdmitted ? "#ffcccc" : "#fff" }}
                                            >
                                                {/* School Name */}
                                                <TableCell sx={{ fontWeight: "bold", border: "1px solid #ddd", padding: "8px" }}>
                                                    {schoolName}
                                                </TableCell>

                                                {/* Program Name */}
                                                <TableCell sx={{ fontWeight: "bold", border: "1px solid #ddd", padding: "8px" }}>
                                                    {programName}
                                                </TableCell>

                                                {/* Specialization Name */}
                                                <TableCell sx={{ border: "1px solid #ddd", padding: "8px" }}>
                                                    {specializationName}
                                                </TableCell>

                                                {/* Fee Category Data */}
                                                {feeCategories.map((category) => (
                                                    <React.Fragment key={category}>
                                                        <TableCell
                                                            align="center"
                                                            sx={{ border: "1px solid #ddd", padding: "8px" }}
                                                        >
                                                            {combinedData[category].intake}
                                                        </TableCell>
                                                        <TableCell
                                                            align="center"
                                                            sx={{ border: "1px solid #ddd", padding: "8px" }}
                                                        >
                                                            {combinedData[category].admitted}
                                                        </TableCell>
                                                        <TableCell
                                                            align="center"
                                                            sx={{ border: "1px solid #ddd", padding: "8px" }}
                                                        >
                                                            {combinedData[category].vacant}
                                                        </TableCell>
                                                    </React.Fragment>
                                                ))}
                                            </TableRow>
                                        );
                                    })}
                                </React.Fragment>
                            ))}
                        </React.Fragment>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

const headerCellSx = {
    color: "#fff",
    fontWeight: "bold",
    border: "1px solid #fff",
    fontSize: "14px",
    textAlign: "center",
    padding: "10px",
};

const bodyCellSx = {
    border: "1px solid #ddd",
    padding: "8px",
    textAlign: "center",
};

const sortAcademicYears = (years = []) =>
    [...years].sort((a, b) => {
        const yearA = parseInt(String(a).split("-")[0], 10) || 0;
        const yearB = parseInt(String(b).split("-")[0], 10) || 0;
        return yearA - yearB;
    });

const buildGenderSchoolPivot = (data = []) => {
    const acYearMeta = {};
    data.forEach((item) => {
        if (!acYearMeta[item.ac_year]) {
            acYearMeta[item.ac_year] = item.ac_year_id;
        }
    });

    const acYears = sortAcademicYears(Object.keys(acYearMeta));
    const schoolMap = new Map();

    data.forEach((item) => {
        if (!schoolMap.has(item.school_id)) {
            schoolMap.set(item.school_id, {
                school_id: item.school_id,
                school_name_short: item.school_name_short,
                school_name: item.school_name,
                years: {},
                total_male: 0,
                total_female: 0,
            });
        }

        const school = schoolMap.get(item.school_id);
        school.years[item.ac_year] = {
            ac_year_id: item.ac_year_id,
            male_count: item.male_count || 0,
            female_count: item.female_count || 0,
            total_student: item.total_student || 0,
        };
        school.total_male += item.male_count || 0;
        school.total_female += item.female_count || 0;
    });

    const schools = [...schoolMap.values()].sort((a, b) =>
        (a.school_name_short || "").localeCompare(
            b.school_name_short || "",
            undefined,
            { sensitivity: "base" },
        ),
    );

    const columnTotals = acYears.reduce(
        (acc, year) => {
            acc[year] = { male: 0, female: 0 };
            return acc;
        },
        {},
    );

    let grandMaleTotal = 0;
    let grandFemaleTotal = 0;

    schools.forEach((school) => {
        acYears.forEach((year) => {
            const yearData = school.years[year];
            if (yearData) {
                columnTotals[year].male += yearData.male_count;
                columnTotals[year].female += yearData.female_count;
            }
        });
        grandMaleTotal += school.total_male;
        grandFemaleTotal += school.total_female;
    });

    return {
        acYears,
        acYearMeta,
        schools,
        columnTotals,
        grandMaleTotal,
        grandFemaleTotal,
    };
};

export const GenderSchoolWiseTable = ({ data = [], onYearCellClick }) => {
    const { acYears, schools, columnTotals, grandMaleTotal, grandFemaleTotal } =
        buildGenderSchoolPivot(data);

    const renderClickableCount = (school, year, count) => {
        const yearData = school.years[year];
        if (!yearData || count === 0) {
            return <Typography variant="body2">{count || 0}</Typography>;
        }

        return (
            <Typography
                variant="body2"
                color="primary"
                sx={{ cursor: "pointer", fontWeight: 500 }}
                onClick={() =>
                    onYearCellClick?.({
                        school_id: school.school_id,
                        school_name_short: school.school_name_short,
                        school_name: school.school_name,
                        ac_year: year,
                        ac_year_id: yearData.ac_year_id,
                    })
                }
            >
                {count}
            </Typography>
        );
    };

    return (
        <TableContainer sx={{ paddingBottom: "30px" }}>
            <Table sx={{ border: "1px solid #ddd" }}>
                <TableHead>
                    <TableRow sx={{ backgroundColor: "#376a7d" }}>
                        <TableCell rowSpan={2} sx={headerCellSx}>
                            School
                        </TableCell>
                        {acYears.map((year) => (
                            <TableCell
                                key={year}
                                colSpan={2}
                                align="center"
                                sx={headerCellSx}
                            >
                                {year}
                            </TableCell>
                        ))}
                        <TableCell colSpan={3} align="center" sx={headerCellSx}>
                            Total
                        </TableCell>
                    </TableRow>
                    <TableRow sx={{ backgroundColor: "#376a7d" }}>
                        {acYears.map((year) => (
                            <React.Fragment key={`${year}-sub`}>
                                <TableCell sx={headerCellSx}>M</TableCell>
                                <TableCell sx={headerCellSx}>F</TableCell>
                            </React.Fragment>
                        ))}
                        <TableCell sx={headerCellSx}>M</TableCell>
                        <TableCell sx={headerCellSx}>F</TableCell>
                        <TableCell sx={headerCellSx}>Total</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {schools.map((school) => (
                        <TableRow
                            key={school.school_id}
                            sx={{
                                "&:hover": { backgroundColor: "#f5f5f5" },
                            }}
                        >
                            <TableCell
                                sx={{
                                    ...bodyCellSx,
                                    fontWeight: "bold",
                                    textAlign: "left",
                                }}
                                title={school.school_name}
                            >
                                {school.school_name_short}
                            </TableCell>
                            {acYears.map((year) => {
                                const yearData = school.years[year];
                                return (
                                    <React.Fragment key={`${school.school_id}-${year}`}>
                                        <TableCell sx={bodyCellSx}>
                                            {renderClickableCount(
                                                school,
                                                year,
                                                yearData?.male_count || 0,
                                            )}
                                        </TableCell>
                                        <TableCell sx={bodyCellSx}>
                                            {renderClickableCount(
                                                school,
                                                year,
                                                yearData?.female_count || 0,
                                            )}
                                        </TableCell>
                                    </React.Fragment>
                                );
                            })}
                            <TableCell
                                sx={{
                                    ...bodyCellSx,
                                    fontWeight: "bold",
                                    backgroundColor: "#fafafa",
                                }}
                            >
                                {school.total_male}
                            </TableCell>
                            <TableCell
                                sx={{
                                    ...bodyCellSx,
                                    fontWeight: "bold",
                                    backgroundColor: "#fafafa",
                                }}
                            >
                                {school.total_female}
                            </TableCell>
                            <TableCell
                                sx={{
                                    ...bodyCellSx,
                                    fontWeight: "bold",
                                    backgroundColor: "#fafafa",
                                }}
                            >
                                {school.total_male + school.total_female}
                            </TableCell>
                        </TableRow>
                    ))}
                    <TableRow sx={{ backgroundColor: "#376a7d" }}>
                        <TableCell
                            sx={{
                                ...headerCellSx,
                                textAlign: "left",
                            }}
                        >
                            Total
                        </TableCell>
                        {acYears.map((year) => (
                            <React.Fragment key={`total-${year}`}>
                                <TableCell sx={headerCellSx}>
                                    {columnTotals[year]?.male || 0}
                                </TableCell>
                                <TableCell sx={headerCellSx}>
                                    {columnTotals[year]?.female || 0}
                                </TableCell>
                            </React.Fragment>
                        ))}
                        <TableCell sx={headerCellSx}>{grandMaleTotal}</TableCell>
                        <TableCell sx={headerCellSx}>{grandFemaleTotal}</TableCell>
                        <TableCell sx={headerCellSx}>
                            {grandMaleTotal + grandFemaleTotal}
                        </TableCell>
                    </TableRow>
                </TableBody>
            </Table>
        </TableContainer>
    );
};
